// src/pages/Dashboard.jsx
//
// Direction A — KPI strip + open tickets panel + compliance donut + portfolio table.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Avatar,
  Button,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import Layout from "../components/layout/Layout";
import Panel from "../components/brand/Panel";
import Badge from "../components/brand/Badge";
import Eyebrow from "../components/brand/Eyebrow";
import KpiTile from "../components/brand/KpiTile";
import { fonts } from "../theme/tokens";

import { useAuth } from "../context/AuthContext";
import { getAccessibleProperties, getComplianceStats } from "../api/dashboard";
import { getTickets, getTicketStats } from "../api/ticketApi";

const greetingFor = (hour) =>
  hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

const formatDateLong = (d = new Date()) =>
  d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).replace(/(\w+) (.+)/, "$1 · $2");

const firstName = (user) => {
  if (!user) return "there";
  if (user.name) return user.name.split(/\s+/)[0];
  return user.username || "there";
};

const getInitials = (name) => {
  if (!name) return "??";
  const numMatch = name.match(/^(\d+)/);
  if (numMatch) return numMatch[1].slice(0, 2);
  return name.slice(0, 2).toUpperCase();
};

const fmtGBP = (n) => "£" + Math.round(n || 0).toLocaleString();

const getComplianceState = (propertyId, complianceStats) => {
  const { expired_items = [], expiring_soon_items = [] } = complianceStats;
  if (expired_items.some((i) => i.property_id === propertyId)) return "expired";
  if (expiring_soon_items.some((i) => i.property_id === propertyId)) return "expiring";
  return "valid";
};

const getContactsState = (property) => {
  const totalUnits =
    property.contact_summary?.total_units ??
    property.metadata?.total_units ??
    property.unit_count ??
    0;
  const validEmailUnits = property.contact_summary?.valid_email_units ?? 0;
  const missingEmailUnits =
    property.contact_summary?.missing_email_units ??
    Math.max(totalUnits - validEmailUnits, 0);
  if (totalUnits === 0 || missingEmailUnits > 0) return "missing";
  return "complete";
};

// Donut chart, in-house SVG (no chart library dependency).
function Donut({ valid, expiring, expired, total, size = 160, strokeWidth = 22 }) {
  const theme = useTheme();
  const r = (size - strokeWidth) / 2;
  const C = 2 * Math.PI * r;
  const sum = Math.max(1, valid + expiring + expired);
  const segs = [
    { c: theme.palette.success.main, l: (valid / sum) * C, off: 0 },
    {
      c: "#f97316",
      l: (expiring / sum) * C,
      off: (valid / sum) * C,
    },
    {
      c: theme.palette.error.main,
      l: (expired / sum) * C,
      off: ((valid + expiring) / sum) * C,
    },
  ];
  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(0,0,0,0.06)"
          strokeWidth={strokeWidth}
        />
        {segs.map(
          (s, i) =>
            s.l > 0 && (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={s.c}
                strokeWidth={strokeWidth}
                strokeDasharray={`${s.l} ${C - s.l}`}
                strokeDashoffset={-s.off}
              />
            )
        )}
      </svg>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            fontFamily: fonts.serif,
            fontSize: 32,
            fontWeight: 600,
            lineHeight: 1,
            color: theme.palette.brand.ink,
          }}
        >
          {total}
        </Box>
        <Box
          sx={{
            fontSize: 10,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: theme.palette.brand.inkSoft,
            mt: 0.5,
          }}
        >
          Properties
        </Box>
      </Box>
    </Box>
  );
}

function LegendRow({ color, label, n, last }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        pb: last ? 0 : 1,
        borderBottom: last ? "none" : `1px solid ${theme.palette.brand.border}`,
      }}
    >
      <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: color }} />
      <Box sx={{ flex: 1, color: theme.palette.brand.inkMuted, fontSize: 13 }}>{label}</Box>
      <Box sx={{ fontSize: 16, fontWeight: 600, fontFamily: fonts.serif }}>{n}</Box>
    </Box>
  );
}

export default function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [properties, setProperties] = useState([]);
  const [openTickets, setOpenTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState({
    total: 0,
    open: 0,
    in_progress: 0,
    high_priority_open: 0,
  });
  const [complianceStats, setComplianceStats] = useState({
    expired_count: 0,
    expiring_soon_count: 0,
    expired_items: [],
    expiring_soon_items: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const propRes = await getAccessibleProperties(true);
        if (cancelled) return;
        const props = propRes?.data || [];
        const unique = [
          ...new Map(props.map((p) => [p.property_id, p])).values(),
        ];
        setProperties(unique);
      } catch (e) {
        console.error("Dashboard: failed to load properties", e);
      }

      try {
        const ticketRes = await getTickets();
        if (!cancelled) setOpenTickets(ticketRes?.results || []);
      } catch (e) {
        console.error("Dashboard: failed to load tickets", e);
      }

      try {
        const stats = await getTicketStats();
        if (!cancelled) setTicketStats(stats || {});
      } catch (e) {
        console.error("Dashboard: failed to load ticket stats", e);
      }

      try {
        const cs = await getComplianceStats(true);
        if (!cancelled) setComplianceStats(cs || {});
      } catch (e) {
        console.error("Dashboard: failed to load compliance stats", e);
      }

      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const liveProperties = useMemo(
    () => properties.filter((p) => p.live !== false),
    [properties]
  );

  const totalLiveUnits = useMemo(
    () =>
      liveProperties.reduce(
        (sum, p) =>
          sum +
          (p.contact_summary?.total_units ??
            p.metadata?.total_units ??
            p.unit_count ??
            0),
        0
      ),
    [liveProperties]
  );

  const complianceCounts = useMemo(() => {
    const counts = { valid: 0, expiring: 0, expired: 0 };
    liveProperties.forEach((p) => {
      const s = getComplianceState(p.property_id, complianceStats);
      counts[s] += 1;
    });
    return counts;
  }, [liveProperties, complianceStats]);

  const openTicketsTop = openTickets.slice(0, 6);

  const RaiseTicketCta = (
    <Button
      variant="contained"
      size="small"
      startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
      onClick={() => navigate("/tickets/new")}
      sx={{ px: 1.75, py: "8px", fontSize: 12.5 }}
    >
      Raise ticket
    </Button>
  );

  return (
    <Layout
      eyebrow={formatDateLong()}
      title={
        <>
          {greetingFor(new Date().getHours())},{" "}
          <Box
            component="em"
            sx={{ fontStyle: "italic", color: theme.palette.brand.goldDeep }}
          >
            {firstName(user)}
          </Box>
          .
        </>
      }
      rightSlot={RaiseTicketCta}
      topBarRight={null}
    >
      {/* KPI STRIP — 5-up */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr", lg: "repeat(5, 1fr)" },
          gap: 1.5,
          mb: 3,
        }}
      >
        <KpiTile
          label="Live properties"
          value={liveProperties.length}
          sub="Active portfolio"
          onClick={() => navigate("/properties")}
        />
        <KpiTile
          label="Live units"
          value={totalLiveUnits}
          sub="Across Kent + SE"
          onClick={() => navigate("/properties")}
        />
        <KpiTile
          label="Open tickets"
          value={(ticketStats.open ?? 0) + (ticketStats.in_progress ?? 0)}
          sub={`${ticketStats.high_priority_open || 0} high priority`}
          tone="warning"
          onClick={() => navigate("/tickets?summary=open")}
        />
        <KpiTile
          label="Expiring soon"
          value={complianceStats.expiring_soon_count || 0}
          sub="Next 30 days"
          tone="warning"
          onClick={() => navigate("/renewals?tab=expiring")}
        />
        <KpiTile
          label="Expired"
          value={complianceStats.expired_count || 0}
          sub="Needs action"
          tone="danger"
          onClick={() => navigate("/renewals?tab=expired")}
        />
      </Box>

      {/* OPEN TICKETS + COMPLIANCE HEALTH */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.7fr 1fr" },
          gap: 2.75,
          mb: 3,
        }}
      >
        <Panel
          eyebrow="Requires attention"
          title="Open tickets"
          right={
            <Box
              component="span"
              onClick={() => navigate("/tickets?summary=open")}
              sx={{
                fontSize: 13,
                color: theme.palette.brand.ink,
                textDecoration: "underline",
                textDecorationColor: theme.palette.brand.gold,
                textUnderlineOffset: "3px",
                cursor: "pointer",
                "&:hover": { textDecorationColor: theme.palette.brand.ink },
              }}
            >
              View all{" "}
              {(ticketStats.open ?? 0) + (ticketStats.in_progress ?? 0)} →
            </Box>
          }
        >
          {openTicketsTop.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: "center",
                color: theme.palette.brand.inkSoft,
                fontSize: 13,
              }}
            >
              {loading ? "Loading tickets…" : "No open tickets — clear queue."}
            </Box>
          ) : (
            openTicketsTop.map((t, i) => (
              <Box
                key={t.id || t.ticket_id}
                onClick={() => navigate(`/tickets?ticket=${t.id || t.ticket_id}`)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "52px 1fr auto auto auto",
                  gap: 1.75,
                  px: 2.25,
                  py: 1.5,
                  borderBottom:
                    i < openTicketsTop.length - 1
                      ? `1px solid ${theme.palette.brand.border}`
                      : "none",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "background .12s",
                  "&:hover": { backgroundColor: theme.palette.brand.surfaceMuted },
                }}
              >
                <Box
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: theme.palette.brand.inkSoft,
                    fontFamily: fonts.mono,
                  }}
                >
                  #{t.id || t.ticket_id}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: theme.palette.brand.ink,
                      lineHeight: 1.3,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {t.title}
                  </Box>
                  <Box
                    sx={{
                      fontSize: 11.5,
                      color: theme.palette.brand.inkSoft,
                      mt: "2px",
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {t.property_name && <span>{t.property_name}</span>}
                    {t.property_name && t.type && <span>·</span>}
                    {t.type && <span>{t.type}</span>}
                  </Box>
                </Box>
                {t.priority && <Badge tone={t.priority}>{t.priority}</Badge>}
                {t.status && <Badge tone={t.status}>{t.status}</Badge>}
                <ChevronRightRoundedIcon
                  sx={{ fontSize: 16, color: theme.palette.brand.inkSoft }}
                />
              </Box>
            ))
          )}
        </Panel>

        <Panel eyebrow="Status" title="Compliance health">
          <Box sx={{ p: 2.75 }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2.25 }}>
              <Donut
                valid={complianceCounts.valid}
                expiring={complianceCounts.expiring}
                expired={complianceCounts.expired}
                total={liveProperties.length}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, fontSize: 13 }}>
              <LegendRow color={theme.palette.success.main} label="Valid" n={complianceCounts.valid} />
              <LegendRow color="#f97316" label="Expiring soon" n={complianceCounts.expiring} />
              <LegendRow color={theme.palette.error.main} label="Expired" n={complianceCounts.expired} last />
            </Box>
          </Box>
        </Panel>
      </Box>

      {/* PORTFOLIO */}
      <Panel
        eyebrow="Portfolio"
        title="Buildings under management"
        right={
          <Box sx={{ fontSize: 13, color: theme.palette.brand.inkSoft }}>
            {liveProperties.length} buildings · {totalLiveUnits} units
          </Box>
        }
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 0.7fr 1.2fr 1fr 1fr 0.9fr",
            gap: 0,
            px: 2.75,
            py: 1.25,
            borderBottom: `1px solid ${theme.palette.brand.border}`,
            backgroundColor: theme.palette.brand.surfaceMuted,
          }}
        >
          {["Property", "Units", "Reserve fund", "Contacts", "Compliance", "Tickets"].map(
            (h, i) => (
              <Eyebrow
                key={h}
                sx={{
                  color: theme.palette.brand.inkSoft,
                  fontSize: 10,
                  textAlign: i === 5 ? "right" : "left",
                }}
              >
                {h}
              </Eyebrow>
            )
          )}
        </Box>

        {liveProperties.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", color: theme.palette.brand.inkSoft }}>
            {loading ? "Loading properties…" : "No properties found."}
          </Box>
        ) : (
          liveProperties.map((p, i) => {
            const compliance = getComplianceState(p.property_id, complianceStats);
            const contacts = getContactsState(p);
            const unitCount =
              p.metadata?.total_units ?? p.unit_count ?? 0;
            const propertyTickets = openTickets.filter(
              (t) => t.property_id === p.property_id
            ).length;
            return (
              <Box
                key={p.property_id}
                onClick={() => navigate(`/properties/${p.property_id}/details`)}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 0.7fr 1.2fr 1fr 1fr 0.9fr",
                  gap: 0,
                  px: 2.75,
                  py: 1.5,
                  borderBottom:
                    i < liveProperties.length - 1
                      ? `1px solid ${theme.palette.brand.border}`
                      : "none",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "background .12s",
                  "&:hover": { backgroundColor: theme.palette.brand.surfaceMuted },
                  fontSize: 13,
                }}
              >
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 34,
                      height: 34,
                      backgroundColor: "rgba(0,0,0,0.06)",
                      color: theme.palette.brand.ink,
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    {getInitials(p.property_name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: theme.palette.brand.ink,
                        lineHeight: 1.25,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {p.property_name}
                    </Box>
                    <Box
                      sx={{
                        fontSize: 11.5,
                        color: theme.palette.brand.inkSoft,
                        mt: "1px",
                      }}
                    >
                      {p.address_line1}
                      {p.city ? ` · ${p.city}` : ""}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ color: theme.palette.brand.ink, fontWeight: 500 }}>
                  {unitCount}
                </Box>
                <Box
                  sx={{
                    color: theme.palette.brand.ink,
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 600,
                  }}
                >
                  {fmtGBP(p.metadata?.reserve_fund || 0)}
                </Box>
                <Box>
                  <Badge tone={contacts}>{contacts}</Badge>
                </Box>
                <Box>
                  <Badge tone={compliance}>{compliance}</Badge>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  {propertyTickets > 0 ? (
                    <Box
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: theme.palette.warning.main,
                        fontFamily: fonts.serif,
                      }}
                    >
                      {propertyTickets}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        fontSize: 11,
                        color: theme.palette.success.main,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                      }}
                    >
                      CLEAR
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })
        )}
      </Panel>
    </Layout>
  );
}
